import { SvgConsumer } from '@components/icons/svgConsumer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Form, FormField, FormMessage } from '@components/ui/form';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/form/input';
import { parseISO } from 'date-fns';
import { formatDate, formatTime } from '@utils/i18n';
import { JobTaskPill } from '@components/job/workProgress/task/pill';
import { ExternalScheduleDialogProps } from './types';
import { ExternalScheduleActions } from './actions';
import { FormRequired } from '@components/ui/form/required';
import { AlertBanner } from '@components/ui/alertBanner';
import { useDropdownsContext } from '@contexts/dropdowns';
import { defaultValues } from '@forms/schedules/external/constants';
import { ExternalScheduleFormValues } from '@forms/schedules/external/types';
import { useErrors } from '@hooks/errors';
import { getHiddenErrorFields } from './helpers';
import { hasDirtyValues } from '@utils/formHelpers';

export const ExternalScheduleDialog = ({
  form,
  onSubmit,
  type,
  open,
  setOpen,
  completed,
  locked,
  children,
  jobId,
  name,
  taskId,
  schedule,
  importantTasks,
  note,
  concreteDirectId,
  extraComment,
  readOnly,
  scheduleType,
}: ExternalScheduleDialogProps) => {
  const {
    companies: { options, disabled: companiesDisabled },
  } = useDropdownsContext();

  const { t } = useTranslation('app');
  const handleErrors = useErrors();
  const initializedFor = useRef<string | null>(null);

  const { setValue, watch, reset } = form;
  const unsavedMaterialChanges = hasDirtyValues(form.formState.dirtyFields.materialOptions);

  const externalContactId = watch('externalContactId');
  const noPhoneNumber = ![
    ...options.companies.flatMap((el) => el.contacts),
    ...options.workflowContacts,
  ].find((el) => el.id === externalContactId)?.phone;

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      reset(defaultValues);
      return;
    }
    const key = `${schedule?.task?.id}-${schedule?.id}`;
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    if (note?.content) setValue('note', note.content);
    if (extraComment) setValue('extraComment', extraComment);
    const date = schedule?.startsAt ? new Date(schedule.startsAt) : new Date();
    setValue('time', date);
    setValue('date', date);
    if (schedule?.status === 'approved') setValue('confirmed', true);
    else setValue('confirmed', false);
  }, [setValue, note, extraComment, schedule, open, reset]);

  const onInvalid = (errors: FieldErrors<ExternalScheduleFormValues>) => {
    const hiddenFields = getHiddenErrorFields(errors, type);
    if (!hiddenFields.length) return;
    Sentry.captureMessage('External schedule form blocked by hidden validation errors', {
      extra: { type, fields: hiddenFields, taskId },
    });
    handleErrors(`form_hidden_errors ${hiddenFields.join(', ')}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader className="gap-1">
          <DialogTitle>{name}</DialogTitle>
          {importantTasks
            ?.sort(
              (a, b) =>
                parseISO(a.schedule?.startsAt || '').getTime() -
                parseISO(b.schedule?.startsAt || '').getTime(),
            )
            .map((task) => (
              <JobTaskPill key={`important-task-${task.id}`} status={task.status}>
                {t('schedules.scheduledAt', {
                  stage: task.name,
                  action: t(`pages.job.info.status.${task.status}`),
                  date: task?.schedule?.startsAt
                    ? formatDate(parseISO(task.schedule.startsAt))
                    : '',
                  time: task?.schedule?.startsAt
                    ? formatTime(parseISO(task.schedule.startsAt))
                    : '',
                })}
              </JobTaskPill>
            ))}
          {externalContactId && !companiesDisabled && noPhoneNumber && (
            <AlertBanner type="tooltip">{t('schedules.noPhoneNumber')}</AlertBanner>
          )}
        </DialogHeader>

        {form.formState.errors.taskId && (
          <div className="flex items-center gap-3 bg-danger-400 px-11 py-3 text-sm font-extrabold text-white">
            <span>
              <SvgConsumer className="text-xl" id="error" />
            </span>
            <span className="flex flex-col">
              <span>{t('schedules.mainContact')}</span>
              <span>{t('schedules.addMainContact')}</span>
            </span>
          </div>
        )}

        {completed && (
          <div className="flex items-center gap-3 bg-success-400 px-11 py-3 text-sm font-extrabold text-white">
            <span>
              <SvgConsumer className="text-xl" id="tooltip" />
            </span>
            {t('schedules.completedTooltip')}
          </div>
        )}

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <div className="space-y-4">
              {children}
              {type !== 'concreteDirectOrder' && (
                <>
                  <FormField
                    control={form.control}
                    name="extraComment"
                    render={({ field }) => (
                      <FormItemWrapper
                        label={
                          <div className="flex items-center gap-2">
                            {t('forms.extraComment.label')}
                            <AppTooltip side="top" context={t('forms.extraComment.tooltip')}>
                              <span>
                                <SvgConsumer className="text-m text-neutral-500" id="tooltip" />
                              </span>
                            </AppTooltip>
                          </div>
                        }>
                        <Input disabled={completed || readOnly} {...field} />
                      </FormItemWrapper>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItemWrapper label={t('forms.note.label')}>
                        <Input disabled={completed || readOnly} {...field} />
                      </FormItemWrapper>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmed"
                    render={({ field: { value, onChange, ...rest } }) => (
                      <FormItemWrapper>
                        <Checkbox
                          {...rest}
                          disabled={completed || readOnly}
                          checked={value}
                          onCheckedChange={onChange}>
                          <div className="flex items-center gap-2">
                            {t('forms.confirmed.label')}
                            <AppTooltip
                              side="top"
                              context={
                                <p>
                                  {[
                                    t('schedules.confirmedTooltip'),
                                    type.includes('inspection') && t('schedules.assigneeTooltip'),
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                </p>
                              }>
                              <span>
                                <SvgConsumer className="text-m text-neutral-500" id="tooltip" />
                              </span>
                            </AppTooltip>
                          </div>
                        </Checkbox>
                      </FormItemWrapper>
                    )}
                  />
                </>
              )}
              <FormRequired />
              <FormField name="root" render={() => <FormMessage />} />
            </div>
            <ExternalScheduleActions
              setOpen={setOpen}
              locked={locked}
              completed={completed}
              jobId={jobId}
              taskId={taskId}
              scheduleId={schedule?.id}
              concreteDirectId={concreteDirectId}
              readOnly={readOnly}
              scheduleType={scheduleType}
              unsavedChanges={unsavedMaterialChanges}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
